import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from "@angular/core"
import {
    SearchSuggestionsResponse,
    SearchSuggestion,
    SearchDocument,
    SearchDocumentCategory,
} from "../../models/search-suggestions.model"
import Utils from "../../utils/utils"

@Component({
    selector: "app-search-suggestions-dropdown",
    templateUrl: "./search-suggestions-dropdown.component.html",
    styleUrl: "./search-suggestions-dropdown.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SearchSuggestionsDropdownComponent implements OnChanges {
    @Input() idPrefix: string = ""
    @Input() suggestions: SearchSuggestionsResponse = { suggestions: [], documents: [] }
    @Input() isOpen: boolean = false
    @Input() activeIndex: number = -1
    @Input() searchTerm: string = ""
    @Output() searchSuggestionSelected = new EventEmitter<string>()
    @Output() documentSelected = new EventEmitter<SearchDocument>()
    @Output() viewAllResults = new EventEmitter<string>()
    @Output() viewAllBlur = new EventEmitter<FocusEvent>()

    private flattenedDocuments: SearchDocument[] = []
    private touchedItem: SearchSuggestion | SearchDocument | "view-all" | null = null

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.suggestions) {
            const items: SearchDocument[] = []
            for (const group of this.suggestions.documents) {
                items.push(...group.documents)
            }
            this.flattenedDocuments = items
        }
    }

    onSelect(text: string): void {
        this.searchSuggestionSelected.emit(text)
    }

    onSelectDocument(document: SearchDocument): void {
        this.documentSelected.emit(document)
    }

    onViewAllResults(): void {
        this.viewAllResults.emit(this.searchTerm)
    }

    onViewAllBlur(event: FocusEvent): void {
        this.viewAllBlur.emit(event)
    }

    onItemTouchStart(item: SearchSuggestion | SearchDocument | "view-all"): void {
        this.touchedItem = item
    }

    isTouched(item: SearchSuggestion | SearchDocument | "view-all"): boolean {
        return this.touchedItem === item
    }

    slugify(value: string | number): string {
        return Utils.slugify(value)
    }

    capitalizeFirstLetter(text: string): string {
        if (!text) {
            return text
        }
        return text.charAt(0).toUpperCase() + text.slice(1)
    }

    isLastSuggestion(index: number): boolean {
        return index === this.suggestions.suggestions.length - 1
    }

    getHighlightParts(text: string): { before: string; match: string; after: string } {
        if (!text || !this.searchTerm) {
            return { before: text, match: "", after: "" }
        }

        const term = this.searchTerm.trim()
        if (!term) {
            return { before: text, match: "", after: "" }
        }

        const lowerText = text.toLowerCase()
        const lowerTerm = term.toLowerCase()
        const matchIndex = lowerText.indexOf(lowerTerm)

        if (matchIndex === -1) {
            return { before: text, match: "", after: "" }
        }

        return {
            before: text.slice(0, matchIndex),
            match: text.slice(matchIndex, matchIndex + term.length),
            after: text.slice(matchIndex + term.length),
        }
    }

    hasMoreResults(): boolean {
        return this.suggestions.documents.length > 0
    }

    hasCategoryLabel(group: SearchDocumentCategory): boolean {
        return !!group.category?.trim()
    }

    isDocumentActive(document: SearchDocument): boolean {
        const offset = this.suggestions.suggestions.length
        const docIndex = this.flattenedDocuments.indexOf(document)
        return offset + docIndex === this.activeIndex
    }

    getDocumentGlobalIndex(document: SearchDocument): number {
        return this.flattenedDocuments.indexOf(document)
    }

    isViewAllActive(): boolean {
        const viewAllIndex = this.suggestions.suggestions.length + this.flattenedDocuments.length
        return viewAllIndex === this.activeIndex
    }
}
